import type { CookieStorage } from '@crossnokaye/ux-common';

import { createCkCookieStorage } from '@/utils/ck-cookie-storage';

const RESTART_PENDING_COOKIE_PREFIX = 'ck.restartPending.';
// Best-effort UX hint that bridges the latency between a restart command and
// the BFF stream reflecting it. The shutdown banner is the source of truth —
// if the cookie expires before the stream catches up, the banner reverts to
// the underlying shutdown state's normal copy, which is still accurate.
const RESTART_PENDING_MAX_AGE_SECONDS = 5 * 60; // 5 minutes

const listeners = new Set<() => void>();

// Lazy module-level singleton: getRestartPending is the getSnapshot for a
// useSyncExternalStore in the shutdown banner, so it fires on every render.
// Defer construction to first call so `window` access stays at runtime.
let cachedStorage: CookieStorage | undefined;
function getStorage(): CookieStorage {
  cachedStorage ??= createCkCookieStorage(RESTART_PENDING_MAX_AGE_SECONDS);
  return cachedStorage;
}

/**
 * Check if a restart is pending for the given agent.
 * Reads the cross-domain cookie shared with Darkwing.
 */
export function getRestartPending(agentId: string): boolean {
  return getStorage().getItem(`${RESTART_PENDING_COOKIE_PREFIX}${agentId}`) === 'true';
}

/**
 * Set or clear the restart pending state for the given agent.
 * Writes to a cross-domain cookie shared with Darkwing (5-minute TTL).
 */
export function setRestartPending(agentId: string, pending: boolean): void {
  const storage = getStorage();
  const key = `${RESTART_PENDING_COOKIE_PREFIX}${agentId}`;
  if (pending) {
    storage.setItem(key, 'true');
  } else {
    storage.removeItem(key);
  }
  listeners.forEach((l) => l());
}

/**
 * Subscribe to restart pending state changes.
 * Used with useSyncExternalStore for reactive cookie reads.
 *
 * Listeners are tab-local: only setRestartPending calls in the same tab
 * notify. Cross-tab sync isn't required because the shutdown-banner stream
 * drives normal renders, which re-read the cookie snapshot on each render.
 */
export function subscribeRestartPending(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
